'use client';

import { c as _c } from "react/compiler-runtime";
import { TEST_IDS } from '@repo/sdk/constants';
import { Button, buttonVariants, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, Field, FieldContent, FieldDescription, FieldError, FieldLabel, Popover, PopoverContent, PopoverTrigger, Spinner } from '@repo/ui/components';
import { ChevronDown, ExternalLink, Plus, Search } from '@repo/ui/icons';
import { cn, type VariantProps } from '@repo/ui/lib/utils';
import { type ReactNode, type Ref } from 'react';
import { ReadOnlyTooltip, useFieldContext, useReadOnly } from '@repo/form';
import { Link } from '@repo/form/adapters/next';
import type { CompanyPickerCompany } from './company-picker-company.ts';
import { CompanyIcons, CompanyResultRow } from './company-result-row.tsx';

/** A company row in the picker's results. */
export const COMPANY_RESULT_ITEM_CLASS_NAME = 'h-auto min-h-24 items-start rounded-none border-b px-3 py-2 last:border-b-0 [&>svg:last-child]:hidden';
export type CompanyPickerProps = {
  className?: string;
  clearLabel?: string;
  companies: readonly CompanyPickerCompany[];
  description?: ReactNode;
  detailsHref?: string;
  disabled?: boolean;
  error?: null | string;
  /** Rows after the results, e.g. companies that need a step before they can be picked. */
  extraResults?: ReactNode;
  label?: ReactNode;
  onClear?: () => void;
  onCreateCompany?: (_search: string) => void;
  onOpenChange: (_open: boolean) => void;
  onRetry: () => void;
  onSearchChange: (_search: string) => void;
  onSelect: (_company: CompanyPickerCompany) => void;
  open: boolean;
  placeholder?: string;
  search: string;
  searchPlaceholder?: string;
  selectedCompany?: CompanyPickerCompany | null;
  showCreateWithResults?: boolean;
  status: 'loading' | 'pending' | 'ready';
  triggerRef?: Ref<HTMLButtonElement>;
  triggerStyle?: 'compact' | 'default' | 'search';
  variant?: VariantProps<typeof buttonVariants>['variant'];
  /** Wraps long company titles in the results instead of truncating them. */
  wrapTitle?: boolean;
};
export const CompanyPicker = t0 => {
  const $ = _c(56);
  const {
    className,
    clearLabel: t1,
    companies,
    description,
    detailsHref,
    disabled: t2,
    error,
    extraResults,
    label,
    onClear,
    onCreateCompany,
    onOpenChange,
    onRetry,
    onSearchChange,
    onSelect,
    open,
    placeholder: t3,
    search,
    searchPlaceholder: t4,
    selectedCompany,
    showCreateWithResults: t5,
    status,
    triggerRef,
    triggerStyle: t6,
    variant: t7,
    wrapTitle: t8
  } = t0;
  const clearLabel = t1 === undefined ? "Unassign company" : t1;
  const disabled = t2 === undefined ? false : t2;
  const placeholder = t3 === undefined ? "-" : t3;
  const searchPlaceholder = t4 === undefined ? "Search company" : t4;
  const showCreateWithResults = t5 === undefined ? false : t5;
  const triggerStyle = t6 === undefined ? "default" : t6;
  const variant = t7 === undefined ? "outline" : t7;
  const wrapTitle = t8 === undefined ? false : t8;
  const isReadOnly = useReadOnly();
  const isLoading = status === "loading";
  const isPending = status === "pending";
  const isReady = status === "ready";
  const hasResults = companies.length > 0;
  const showChevron = triggerStyle !== "compact";
  const showClear = Boolean(onClear && selectedCompany);
  const showCreate = isReady && !error && Boolean(onCreateCompany) && (!hasResults || showCreateWithResults);
  const showSearchIcon = triggerStyle === "search";
  let t9;
  if ($[0] !== onOpenChange || $[1] !== onSearchChange) {
    t9 = () => {
      onOpenChange(false);
      onSearchChange("");
    };
    $[0] = onOpenChange;
    $[1] = onSearchChange;
    $[2] = t9;
  } else {
    t9 = $[2];
  }
  const close = t9;
  let t10;
  if ($[3] !== onOpenChange || $[4] !== onSearchChange) {
    t10 = nextOpen => {
      onOpenChange(nextOpen);
      if (!nextOpen) {
        onSearchChange("");
      }
    };
    $[3] = onOpenChange;
    $[4] = onSearchChange;
    $[5] = t10;
  } else {
    t10 = $[5];
  }
  const handleOpenChange = t10;
  let t11;
  if ($[6] !== close || $[7] !== onClear) {
    t11 = () => {
      onClear?.();
      close();
    };
    $[6] = close;
    $[7] = onClear;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  const handleClear = t11;
  let t12;
  if ($[9] !== close || $[10] !== onCreateCompany || $[11] !== search) {
    t12 = () => {
      onCreateCompany?.(search);
      close();
    };
    $[9] = close;
    $[10] = onCreateCompany;
    $[11] = search;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  const handleCreate = t12;
  let t13;
  if ($[13] !== close || $[14] !== onSelect) {
    t13 = company => {
      onSelect(company);
      close();
    };
    $[13] = close;
    $[14] = onSelect;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  const handleSelect = t13;
  let t14;
  if ($[16] !== detailsHref || $[17] !== label || $[18] !== selectedCompany) {
    t14 = (label || detailsHref && selectedCompany) && <div className={cn("flex justify-end", label && "justify-between")}>{label && <FieldLabel>{label}</FieldLabel>}{detailsHref && selectedCompany && <Button nativeButton={false} render={<Link href={detailsHref} rel="noreferrer" target="_blank" />} size="icon-xs" variant="ghost"><ExternalLink /></Button>}</div>;
    $[16] = detailsHref;
    $[17] = label;
    $[18] = selectedCompany;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== className || $[21] !== clearLabel || $[22] !== companies || $[23] !== disabled || $[24] !== error || $[25] !== extraResults || $[26] !== handleClear || $[27] !== handleCreate || $[28] !== handleOpenChange || $[29] !== handleSelect || $[30] !== hasResults || $[31] !== isLoading || $[32] !== isPending || $[33] !== isReadOnly || $[34] !== isReady || $[35] !== onRetry || $[36] !== onSearchChange || $[37] !== open || $[38] !== placeholder || $[39] !== search || $[40] !== searchPlaceholder || $[41] !== selectedCompany || $[42] !== showChevron || $[43] !== showClear || $[44] !== showCreate || $[45] !== showSearchIcon || $[46] !== triggerRef || $[47] !== variant || $[48] !== wrapTitle) {
    t15 = isReadOnly ? <ReadOnlyTooltip><span className="inline-flex min-w-0 items-center gap-1.5">{selectedCompany?.title ?? "-"}{selectedCompany ? <CompanyIcons company={selectedCompany} iconClassName="size-4" /> : null}</span></ReadOnlyTooltip> : <Popover onOpenChange={disabled ? undefined : handleOpenChange} open={disabled ? false : open}><PopoverTrigger asChild={true}><Button className={cn("h-8 justify-between gap-1.5 py-1 pr-1 pl-2.5", className)} data-testid={TEST_IDS.company.inputTrigger} disabled={disabled || isPending} ref={triggerRef} type="button" variant={variant}><span className={cn("flex min-w-0 items-center gap-1.5 truncate", !selectedCompany && "text-muted-foreground")}>{showSearchIcon && <Search className="size-4 shrink-0" />}<span className="truncate">{selectedCompany?.title ?? placeholder}</span>{selectedCompany ? <CompanyIcons company={selectedCompany} iconClassName="size-4" /> : null}</span><span className="flex shrink-0 items-center gap-1">{isPending && <Spinner className="size-4" />}{showChevron && <ChevronDown className="size-4" />}</span></Button></PopoverTrigger><PopoverContent align="start" className="w-112 p-1" collisionPadding={10} side="bottom"><Command shouldFilter={false}><CommandInput autoFocus={true} data-testid={TEST_IDS.company.commandInput} disabled={disabled} onValueChange={onSearchChange} placeholder={searchPlaceholder} value={search} /><CommandList className="max-h-96 overflow-y-auto">{isLoading && <div className="flex justify-center py-6"><Spinner /></div>}{error ? <div className="flex items-center justify-between gap-3 px-2 py-3" role="alert"><p className="text-sm text-destructive">{error}</p><Button onClick={onRetry} size="sm" type="button" variant="outline">Retry</Button></div> : null}{isReady && !error && !hasResults && !extraResults && <CommandEmpty>No companies found.</CommandEmpty>}{isReady && !error && <>{showClear && <CommandGroup><CommandItem onSelect={handleClear} value="clear-company">{clearLabel}</CommandItem></CommandGroup>}{showClear && (hasResults || showCreate) && <CommandSeparator />}{hasResults && <CommandGroup>{companies.map(company_0 => <CommandItem className={COMPANY_RESULT_ITEM_CLASS_NAME} key={company_0.id} onSelect={() => handleSelect(company_0)} value={String(company_0.id)}><CompanyResultRow company={company_0} wrapTitle={wrapTitle} /></CommandItem>)}</CommandGroup>}{extraResults}{showCreate && (hasResults || extraResults) && <CommandSeparator />}{showCreate && <CommandGroup><CommandItem className="flex items-center gap-2" onSelect={handleCreate} value="create-company"><Plus className="text-muted-foreground" /> New company</CommandItem></CommandGroup>}</>}</CommandList></Command></PopoverContent></Popover>;
    $[20] = className;
    $[21] = clearLabel;
    $[22] = companies;
    $[23] = disabled;
    $[24] = error;
    $[25] = extraResults;
    $[26] = handleClear;
    $[27] = handleCreate;
    $[28] = handleOpenChange;
    $[29] = handleSelect;
    $[30] = hasResults;
    $[31] = isLoading;
    $[32] = isPending;
    $[33] = isReadOnly;
    $[34] = isReady;
    $[35] = onRetry;
    $[36] = onSearchChange;
    $[37] = open;
    $[38] = placeholder;
    $[39] = search;
    $[40] = searchPlaceholder;
    $[41] = selectedCompany;
    $[42] = showChevron;
    $[43] = showClear;
    $[44] = showCreate;
    $[45] = showSearchIcon;
    $[46] = triggerRef;
    $[47] = variant;
    $[48] = wrapTitle;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] !== description) {
    t16 = description && <FieldDescription>{description}</FieldDescription>;
    $[50] = description;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] !== t14 || $[53] !== t15 || $[54] !== t16) {
    t17 = <>{t14}{t15}{t16}</>;
    $[52] = t14;
    $[53] = t15;
    $[54] = t16;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  return t17;
};
export type FormCompanyPickerProps = Omit<CompanyPickerProps, 'onClear' | 'onSelect'> & {
  onClear?: () => void;
  onSelect?: (_company: CompanyPickerCompany) => void;
};
export const FormCompanyPicker = t0 => {
  const $ = _c(19);
  let onClear;
  let onSelect;
  let props;
  if ($[0] !== t0) {
    ({
      onClear,
      onSelect,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onClear;
    $[2] = onSelect;
    $[3] = props;
  } else {
    onClear = $[1];
    onSelect = $[2];
    props = $[3];
  }
  const field = useFieldContext();
  let t1;
  if ($[4] !== field || $[5] !== onClear) {
    t1 = onClear ? () => {
      field.handleChange(null);
      onClear();
    } : undefined;
    $[4] = field;
    $[5] = onClear;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const handleClear = t1;
  let t2;
  if ($[7] !== field || $[8] !== onSelect) {
    t2 = company => {
      field.handleChange(company.id);
      onSelect?.(company);
    };
    $[7] = field;
    $[8] = onSelect;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handleSelect = t2;
  let t3;
  if ($[10] !== handleClear || $[11] !== handleSelect || $[12] !== props) {
    t3 = <FieldContent data-sentry-element="FieldContent" data-sentry-source-file="company-picker.tsx"><CompanyPicker {...props} onClear={handleClear} onSelect={handleSelect} data-sentry-element="CompanyPicker" data-sentry-source-file="company-picker.tsx" /></FieldContent>;
    $[10] = handleClear;
    $[11] = handleSelect;
    $[12] = props;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== field.state.meta.errors) {
    t4 = <FieldError errors={field.state.meta.errors} data-sentry-element="FieldError" data-sentry-source-file="company-picker.tsx" />;
    $[14] = field.state.meta.errors;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== t3 || $[17] !== t4) {
    t5 = <Field className="gap-1" data-sentry-element="Field" data-sentry-component="FormCompanyPicker" data-sentry-source-file="company-picker.tsx">{t3}{t4}</Field>;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
};